import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FileText, CheckCircle, XCircle, Clock, Mail, Phone, Calendar, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { adminApi } from '../services/api';
import { FairManagerApplicationResponse as BaseApplicationResponse, FairManagerApplicationUpdate } from '../types/generated';
import { showSuccess, showError } from '../utils/toast';
import { extractApiError } from '../utils/errorUtils';
import OnboardingStatusBadge from './OnboardingStatusBadge';

// Extended interface to include fields from backend that aren't in Pydantic model
interface FairManagerApplicationResponse extends BaseApplicationResponse {
  fair_name?: string;
  user_email?: string;
  reviewer_email?: string;
}

interface OnboardingStatusInfo {
  status: 'not_started' | 'in_progress' | 'completed';
  current_step?: string;
  percentage?: number;
  completed_at?: string | null;
}

export default function AdminManagerApplications() {
  const [applications, setApplications] = useState<FairManagerApplicationResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [selectedApplication, setSelectedApplication] = useState<FairManagerApplicationResponse | null>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [intendedAction, setIntendedAction] = useState<'approved' | 'rejected' | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  // Onboarding status tracking (Issue #1455)
  const [onboardingStatuses, setOnboardingStatuses] = useState<Record<number, OnboardingStatusInfo>>({});

  const loadApplications = useCallback(async () => {
    try {
      setLoading(true);
      const params: { status?: string; limit: number; offset: number } = {
        limit,
        offset: (page - 1) * limit,
      };
      if (filter !== 'all') {
        params.status = filter;
      }
      const data = await adminApi.getAllManagerApplications(params);
      setApplications(data.items);
      setTotal(data.total);
      setError(null);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to load manager applications:', apiError);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  }, [filter, page, limit]);

  // Load onboarding status for approved applications (Issue #1455)
  const loadOnboardingStatuses = useCallback(async () => {
    const approvedApplications = applications.filter(app => app.status === 'approved');

    for (const app of approvedApplications) {
      try {
        const response = await adminApi.getApplicationOnboardingStatus(app.id);

        if (response.onboarding) {
          const { onboarding, progress } = response;
          const statusInfo: OnboardingStatusInfo = {
            status: onboarding.completed ? 'completed' : 'in_progress',
            current_step: progress.current_step,
            percentage: progress.percentage,
            completed_at: onboarding.completed_at,
          };
          setOnboardingStatuses(prev => ({ ...prev, [app.id]: statusInfo }));
        } else {
          // Application approved but onboarding not started
          setOnboardingStatuses(prev => ({
            ...prev,
            [app.id]: { status: 'not_started' },
          }));
        }
      } catch (err: unknown) {
        // Silently fail - onboarding status is optional information
        console.debug(`Could not load onboarding status for application ${app.id}`, err);
      }
    }
  }, [applications]);

  // Calculate counts for filter tabs (memoized for performance)
  const statusCounts = useMemo(() => ({
    all: applications.length,
    pending: applications.filter(app => app.status === 'pending').length,
    approved: applications.filter(app => app.status === 'approved').length,
    rejected: applications.filter(app => app.status === 'rejected').length,
  }), [applications]);

  // Calculate pagination info
  const totalPages = Math.ceil(total / limit);
  const startIndex = (page - 1) * limit + 1;
  const endIndex = Math.min(page * limit, total);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setPage(1);
  }, [filter]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  // Load onboarding statuses after applications are loaded (Issue #1455)
  useEffect(() => {
    if (applications.length > 0) {
      loadOnboardingStatuses();
    }
  }, [applications, loadOnboardingStatuses]);

  // Define closeModal before useEffect hooks that use it
  const closeModal = useCallback(() => {
    setShowReviewModal(false);
    setSelectedApplication(null);
    setIntendedAction(null);
    setReviewNotes('');
  }, []);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showReviewModal && !submitting) {
        closeModal();
      }
    };

    if (showReviewModal) {
      document.addEventListener('keydown', handleEscKey);
      return () => document.removeEventListener('keydown', handleEscKey);
    }
  }, [showReviewModal, submitting, closeModal]);

  // Focus trap for modal (accessibility)
  useEffect(() => {
    if (showReviewModal && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      const handleTabKey = (event: KeyboardEvent) => {
        if (event.key !== 'Tab') return;

        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement?.focus();
            event.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement?.focus();
            event.preventDefault();
          }
        }
      };

      // Focus first element when modal opens
      firstElement?.focus();

      document.addEventListener('keydown', handleTabKey);
      return () => document.removeEventListener('keydown', handleTabKey);
    }
  }, [showReviewModal]);

  // Define handleReview before keyboard shortcuts effect that uses it
  const handleReview = useCallback((application: FairManagerApplicationResponse, action: 'approved' | 'rejected') => {
    setSelectedApplication(application);
    setIntendedAction(action);
    setShowReviewModal(true);
    setReviewNotes('');
  }, []);

  // Keyboard shortcuts for approve/reject (power users)
  useEffect(() => {
    const handleKeyboardShortcut = (event: KeyboardEvent) => {
      // Don't trigger shortcuts when:
      // - Modal is open
      // - User is typing in input/textarea
      // - Modifier keys are pressed
      const target = event.target as HTMLElement;
      const isTyping = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';

      if (showReviewModal || isTyping || event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }

      const pendingApplications = applications.filter(app => app.status === 'pending');
      if (pendingApplications.length === 0) {
        return;
      }

      const firstPendingApp = pendingApplications[0];

      // A = Approve first pending application
      if (event.key === 'a' || event.key === 'A') {
        event.preventDefault();
        handleReview(firstPendingApp, 'approved');
      }

      // R = Reject first pending application
      if (event.key === 'r' || event.key === 'R') {
        event.preventDefault();
        handleReview(firstPendingApp, 'rejected');
      }
    };

    document.addEventListener('keydown', handleKeyboardShortcut);
    return () => document.removeEventListener('keydown', handleKeyboardShortcut);
  }, [applications, showReviewModal, handleReview]);

  const submitReview = async (status: 'approved' | 'rejected') => {
    if (!selectedApplication) return;

    try {
      setSubmitting(true);
      const updateData: FairManagerApplicationUpdate = {
        status,
        review_notes: reviewNotes.trim() || null,
      };

      await adminApi.reviewManagerApplication(selectedApplication.id, updateData);

      showSuccess(
        `Application ${status}`,
        `Application from ${selectedApplication.applicant_name} has been ${status}.`
      );

      closeModal();
      loadApplications();
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to review application:', apiError);
      showError('Failed to review application', apiError.message);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="h-3 w-3 mr-1" />
            Rejected
          </span>
        );
      default:
        return status;
    }
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="text-center py-8" data-testid="applications-loading">
        <p className="text-gray-500">Loading applications...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8" data-testid="applications-error">
        <p className="text-red-500">{error}</p>
        <button
          onClick={loadApplications}
          className="mt-4 px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 w-full max-w-full overflow-hidden" data-testid="admin-manager-applications">
      {/* Header */}
      <div className="bg-white shadow-sm rounded-lg p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold text-gray-900" data-testid="applications-title">
                Fair Manager Applications
              </h1>
              {applications.some(app => app.status === 'pending') && (
                <span className="hidden md:inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-600 border border-gray-300">
                  Keyboard: <kbd className="ml-1 px-1 bg-white border border-gray-300 rounded">A</kbd> Approve{' '}
                  <kbd className="ml-1 px-1 bg-white border border-gray-300 rounded">R</kbd> Reject
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 mt-1">
              Review applications from users who want to manage seeded fairs
            </p>
          </div>

          {/* Filter tabs */}
          <div className="flex space-x-2">
            <button
              onClick={() => setFilter('all')}
              aria-label="Show all applications"
              aria-pressed={filter === 'all'}
              className={`px-3 py-1 text-sm rounded ${
                filter === 'all'
                  ? 'bg-primary text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              All ({statusCounts.all})
            </button>
            <button
              onClick={() => setFilter('pending')}
              aria-label="Show pending applications"
              aria-pressed={filter === 'pending'}
              className={`px-3 py-1 text-sm rounded ${
                filter === 'pending'
                  ? 'bg-yellow-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Pending ({statusCounts.pending})
            </button>
            <button
              onClick={() => setFilter('approved')}
              aria-label="Show approved applications"
              aria-pressed={filter === 'approved'}
              className={`px-3 py-1 text-sm rounded ${
                filter === 'approved'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Approved ({statusCounts.approved})
            </button>
            <button
              onClick={() => setFilter('rejected')}
              aria-label="Show rejected applications"
              aria-pressed={filter === 'rejected'}
              className={`px-3 py-1 text-sm rounded ${
                filter === 'rejected'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Rejected ({statusCounts.rejected})
            </button>
          </div>
        </div>
      </div>

      {/* Applications list */}
      {applications.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm" data-testid="no-applications">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No applications found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {applications.map((application) => (
            <div
              key={application.id}
              className="bg-white shadow-sm rounded-lg p-4 hover:shadow-md transition-shadow"
              data-testid="application-card"
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                {/* Application details */}
                <div className="flex-1 space-y-3">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {application.applicant_name}
                        </h3>
                        <span className="text-xs text-gray-500">
                          (ID: {application.id})
                        </span>
                      </div>
                      {application.fair_name && (
                        <Link
                          to={`/fair/${application.fair_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-secondary hover:text-primary-dark font-medium mb-1"
                        >
                          Fair: {application.fair_name}
                          <ExternalLink className="h-3 w-3" />
                        </Link>
                      )}
                      <p className="text-sm text-gray-600">
                        Applied: {formatDate(application.created_at)}
                      </p>
                    </div>
                    {getStatusBadge(application.status)}
                  </div>

                  {/* Contact info */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center text-gray-700">
                      <Mail className="h-4 w-4 mr-2 text-gray-400" />
                      {application.email}
                    </div>
                    <div className="flex items-center text-gray-700">
                      <Phone className="h-4 w-4 mr-2 text-gray-400" />
                      {application.phone}
                    </div>
                  </div>

                  {/* Relationship explanation */}
                  <div className="bg-gray-50 rounded p-3">
                    <p className="text-sm font-medium text-gray-700 mb-1">
                      Relationship to Fair:
                    </p>
                    <p className="text-sm text-gray-600">{application.relationship_explanation}</p>
                  </div>

                  {/* Review info (if reviewed) */}
                  {application.reviewed_at && (
                    <div className="border-t pt-3 mt-3">
                      <div className="flex items-center text-sm text-gray-600 mb-2">
                        <Calendar className="h-4 w-4 mr-2" />
                        Reviewed: {formatDate(application.reviewed_at)}
                        {application.reviewer_email && (
                          <span className="ml-2">by {application.reviewer_email}</span>
                        )}
                      </div>
                      <div className="bg-secondary-light/10 rounded p-2">
                        <p className="text-sm font-medium text-gray-700 mb-1">Review Notes:</p>
                        <p className="text-sm text-gray-600">
                          {application.review_notes || <em className="text-gray-500">No review notes provided</em>}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Onboarding status (Issue #1455) - Show for approved applications */}
                  {application.status === 'approved' && onboardingStatuses[application.id] && (
                    <div className="border-t pt-3 mt-3">
                      <p className="text-sm font-medium text-gray-700 mb-2">Onboarding Status:</p>
                      <OnboardingStatusBadge status={onboardingStatuses[application.id]} />
                    </div>
                  )}
                </div>

                {/* Actions */}
                {application.status === 'pending' && (
                  <div className="flex sm:flex-col gap-2">
                    <button
                      onClick={() => handleReview(application, 'approved')}
                      className="flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                      data-testid="approve-button"
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Approve
                    </button>
                    <button
                      onClick={() => handleReview(application, 'rejected')}
                      className="flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                      data-testid="reject-button"
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > limit && (
        <div className="bg-white shadow-sm rounded-lg p-4 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="pagination">
          <div className="text-sm text-gray-700 text-center sm:text-left">
            Showing <span className="font-medium">{startIndex}</span> to <span className="font-medium">{endIndex}</span> of{' '}
            <span className="font-medium">{total}</span> applications
          </div>

          <div className="flex items-center gap-2 flex-wrap justify-center">
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="flex items-center px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">Previous</span>
            </button>

            <div className="flex items-center gap-1 flex-wrap">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
                // Show first page, last page, current page, and pages around current
                const showPage =
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= page - 1 && pageNum <= page + 1);

                const showEllipsis =
                  (pageNum === 2 && page > 3) ||
                  (pageNum === totalPages - 1 && page < totalPages - 2);

                if (showEllipsis) {
                  return <span key={pageNum} className="px-2 text-gray-500">...</span>;
                }

                if (!showPage) {
                  return null;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`px-3 py-1 text-sm rounded ${
                      page === pageNum
                        ? 'bg-primary text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                    aria-label={`Page ${pageNum}`}
                    aria-current={page === pageNum ? 'page' : undefined}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              className="flex items-center px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Next page"
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRight className="h-4 w-4 sm:ml-1" />
            </button>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && selectedApplication && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50 p-4"
          data-testid="review-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          onClick={(e) => {
            // Close modal when clicking on backdrop (not the modal content)
            if (e.target === e.currentTarget && !submitting) {
              closeModal();
            }
          }}
        >
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-lg w-full" ref={modalRef}>
            <h3 id="modal-title" className="text-lg font-medium text-gray-900 mb-4">
              {intendedAction === 'approved' ? 'Approve' : 'Reject'} Application
            </h3>

            {/* Application Summary */}
            <div className="bg-gray-50 rounded p-3 mb-4 space-y-2">
              <p className="text-sm">
                <span className="font-medium text-gray-700">Applicant:</span> {selectedApplication.applicant_name}
              </p>
              {selectedApplication.fair_name && (
                <p className="text-sm">
                  <span className="font-medium text-gray-700">Fair:</span> {selectedApplication.fair_name}
                </p>
              )}
              <p className="text-sm">
                <span className="font-medium text-gray-700">Email:</span> {selectedApplication.email}
              </p>
              <p className="text-sm">
                <span className="font-medium text-gray-700">Phone:</span> {selectedApplication.phone}
              </p>
            </div>

            {/* Review notes */}
            <div className="mb-4">
              <label htmlFor="review-notes" className="block text-sm font-medium text-gray-700 mb-2">
                Review Notes (Optional)
              </label>
              <textarea
                id="review-notes"
                rows={4}
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="Add any notes or feedback for the applicant (will be included in email notification)..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                aria-describedby="notes-description"
              />
              <p id="notes-description" className="text-xs text-gray-500 mt-1">
                Optional notes will be sent to the applicant via email
              </p>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              {intendedAction === 'approved' ? (
                <button
                  onClick={() => submitReview('approved')}
                  disabled={submitting}
                  className="flex-1 flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  data-testid="confirm-approve-button"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {submitting ? 'Approving...' : 'Approve'}
                </button>
              ) : (
                <button
                  onClick={() => submitReview('rejected')}
                  disabled={submitting}
                  className="flex-1 flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                  data-testid="confirm-reject-button"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  {submitting ? 'Rejecting...' : 'Reject'}
                </button>
              )}
              <button
                onClick={closeModal}
                disabled={submitting}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50"
                data-testid="cancel-review-button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
